import Datatable from "components/Common/dataTable";
import { useTranslation } from "react-i18next";
import React from "react";
import { Tag } from "antd";
import { formatToLocaleDate } from "utils/dateUtils";
import ActionsDropdown, { Action } from "components/Common/ActionsDropdown";
import { showConfirmDialog } from "utils/confirmDialogUtils";
import UserProfileHeader from "components/Common/ui/UserProfileHeader";
import { useAbility } from "config/abilityContext";

type Props = {
    dataSource: any[];
    loading?: boolean;
    onEdit?: (record: any) => void;
    onDelete?: (record: any) => void;
    dataPaginate: any;
    onChange: (pg: any) => void;
};

const AuthorizedPickUpPersonTable: React.FC<Props> = ({
    dataSource,
    loading,
    onEdit,
    onDelete,
    dataPaginate,
    onChange
}) => {
    const { t } = useTranslation();
    const ability = useAbility();

    const handleDelete = (record: any) => {
        showConfirmDialog({
            title: t("authorizedPickUpPerson.confirmDelete.title"),
            content: t("authorizedPickUpPerson.confirmDelete.message"),
            onOk: () => onDelete?.(record.id),
            okText: t("yes"),
            cancelText: t("no"),
            okType: "danger",
            loading: loading,
        });
    };

    const columns = [
        {
            title: t("authorizedPickUpPerson.form.fullName"),
            dataIndex: "fullName",
            key: "fullName",
        },
        {
            title: t("authorizedPickUpPerson.form.cinNumber"),
            dataIndex: "cinNumber",
            key: "cinNumber",
        },
        {
            title: t("authorizedPickUpPerson.form.phone"),
            dataIndex: "phone",
            key: "phone",
        },
        {
            title: t("authorizedPickUpPerson.form.relationship"),
            dataIndex: "relationship",
            key: "relationship",
            render: (relationship: string) => (
                <Tag color="blue">
                    {t(`authorizedPickUpPerson.relationship.${relationship}`)}
                </Tag>
            ),
        },
        {
            title: t("authorizedPickUpPerson.form.guardian"),
            key: "guardian",
            render: (_: any, record: any) => (
                <UserProfileHeader
                    firstName={record.guardian?.user?.firstName}
                    lastName={record.guardian?.user?.lastName}
                    photo={record.guardian?.user?.photo}
                    subtitle={record.guardian?.user?.phoneNumber}
                />
            ),
        },
        // {
        //     title: t("authorizedPickUpPerson.form.isDefaultGuardian"),
        //     dataIndex: "isDefaultGuardian",
        //     key: "isDefaultGuardian",
        //     align: "center" as const,
        //     render: (isDefault: boolean) => (
        //         <Tag color={isDefault ? "green" : "default"}>
        //             {isDefault ? t("yes") : t("no")}
        //         </Tag>
        //     ),
        // },
        {
            title: t("authorizedPickUpPerson.form.createdAt"),
            dataIndex: "createdAt",
            key: "createdAt",
            render: (v: string) => formatToLocaleDate(v) ?? "—",
        },
        {
            title: t("actions"),
            key: "actions",
            hidden: !onEdit && !onDelete || (!ability.can("update", "AuthorizedPickUpPerson") && !ability.can("delete", "AuthorizedPickUpPerson")),
            render: (_: string, record: any) => {
                const actions: Action[] = [
                    {
                        label: t("edit"),
                        icon: "ti ti-edit",
                        actionType: "link",
                        hidden: !onEdit || !ability.can("update", "AuthorizedPickUpPerson"),
                        onClick: () => onEdit?.(record),
                    },
                    {
                        label: t("delete"),
                        icon: "ti ti-trash",
                        actionType: "link",
                        hidden: !onDelete || !ability.can("delete", "AuthorizedPickUpPerson"),
                        onClick: () => handleDelete(record),
                    },
                ];
                return <ActionsDropdown actions={actions} />;
            },
        },
    ];

    return (
        <div className="table-responsive">
            <Datatable
                rowKey="id"
                columns={columns}
                dataSource={dataSource}
                loading={loading}
                pagination={true}
                dataPaginate={dataPaginate}
                onChange={onChange}
            />
        </div>
    );
};

export default AuthorizedPickUpPersonTable;